/**
 * Pages reachable from a WhatsApp/SMS link, where the visitor is anonymous and the record id is in
 * the URL. Kept separate from the fetching layer so client bundles can classify a route without
 * pulling in the server-side prefetch code.
 */
export type SharedLinkKind =
    | 'order'
    | 'membership'
    | 'package'
    | 'giftCard'
    | 'advanceBalance'
    | 'postcare';

export const SHARED_LINK_PAGE_KINDS: Record<string, SharedLinkKind> = {
    invoice: 'order',
    feedback: 'order',
    payment: 'order',
    orderconfirmation: 'order',
    membershipinvoice: 'membership',
    packageinvoice: 'package',
    giftcardinvoice: 'giftCard',
    advanceinvoice: 'advanceBalance',
    advancerefund: 'advanceBalance',
    balanceinvoice: 'advanceBalance',
    postcare: 'postcare'
};

export type SharedLinkRequest = {
    page: string;
    kind: SharedLinkKind;
    id: string;
};

/**
 * Path-based variant for code that runs before the router hydrates. Shared links are always
 * /<tenant>/<store>/<page>/<id>, so only the third segment is considered.
 */
export const isSharedLinkPath = (pathname?: string): boolean => {
    if (!pathname) return false;
    const page = pathname.split('/').filter(Boolean)[2];
    return Boolean(page) && Boolean(SHARED_LINK_PAGE_KINDS[page.toLowerCase()]);
};

/**
 * Shared links are /<tenant>/<store>/<page>/<id>, but the id also arrives as ?id= on some older
 * links, so both shapes are accepted.
 */
export const resolveSharedLinkRequest = (query: any): SharedLinkRequest | null => {
    const pagepath = query?.pagepath;
    if (!Array.isArray(pagepath) || !pagepath.length) return null;

    const page = String(pagepath[0]).toLowerCase();
    const kind = SHARED_LINK_PAGE_KINDS[page];
    if (!kind) return null;

    const queryId = Array.isArray(query.id) ? query.id[0] : query.id;
    const id = pagepath[1] || queryId || '';
    if (!id) return null;

    return { page, kind, id };
};
